import Vue from 'vue';
import store from '@app/store';

import {
    AlertPlugin,
    FormPlugin,
    FormInputPlugin,
    FormCheckboxPlugin,
    ButtonPlugin,
    CardPlugin,
    CarouselPlugin,
    CollapsePlugin,
    DropdownPlugin,
    EmbedPlugin,
    ListGroupPlugin,
    ModalPlugin,
    NavbarPlugin,
    TabsPlugin,
    TooltipPlugin
} from 'bootstrap-vue';

import VueLazyload from 'vue-lazyload';

// Custom directives
import ClickOutside from './directives/ClickOutside';
import LinkHash from './directives/LinkHash';
// API handling
import Api from './scripts/api';
import ApiErrorFeedback from './scripts/api-error-feedback.ts';
import { ApiSentryReporting } from './scripts/api-sentry.ts';

import './styles/main.scss';

import './scripts/t3oodle';

import './scripts/sentry';
import './scripts/tracking';

import './scripts/css-lazyloading';
import SearchHighlight from './directives/SearchHighlight';
import { useBreakpoints } from '@vueuse/core';
import debounce from 'lodash/debounce';

// Here we import the Vue Formulate library and the components for the my-scholarship form
// This  should be move to pages/my-scholarship/index.js
import VueFormulate from '@braid/vue-formulate';
import StinaText from './components/my-scholarship/StinaForms/StinaText.vue';
import StinaTextarea from './components/my-scholarship/StinaForms/StinaTextarea.vue';
import StinaFileUpload from './components/my-scholarship/StinaForms/StinaFileUpload.vue';

Vue.component('StinaText', StinaText);
Vue.component('StinaTextarea', StinaTextarea);
Vue.component('StinaFileUpload', StinaFileUpload);

// Let Vue Formulate know which slot you want to override for a given type
Vue.use(VueFormulate, {
    library: {
        // the `type` of input you’re targeting.
        Text: {
            component: 'StinaText',
        },
        Textarea: {
            component: 'StinaTextarea',
        },
        Upload: {
            component: 'StinaFileUpload',
        },
    },
});

ApiErrorFeedback.enable();
Api.version(1).addErrorHandler(new ApiSentryReporting());

// implement common bootstrap components like this
Vue.use(AlertPlugin);
Vue.use(ButtonPlugin);
Vue.use(CardPlugin);
Vue.use(CarouselPlugin);
Vue.use(CollapsePlugin);
Vue.use(DropdownPlugin);
Vue.use(EmbedPlugin);
Vue.use(FormPlugin);
Vue.use(FormCheckboxPlugin);
Vue.use(FormInputPlugin);
Vue.use(ListGroupPlugin);
Vue.use(ModalPlugin);
Vue.use(NavbarPlugin);
Vue.use(TabsPlugin);
Vue.use(TooltipPlugin);

// Use custom directives
Vue.use(SearchHighlight);
Vue.use(ClickOutside);
Vue.use(LinkHash);

// Global loaded components
Vue.use(VueLazyload);

const breakpoints = useBreakpoints({
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
});

// default vue configuration for all entry points (pages)
export const config = {
    el: '#app-root',
    store,
    // commonly used components
    components: {
        ActivityStreamWidget: () => import('./components/dashboard/ActivityStreamWidget'),
        AdminSelectList: () => import('./components/AdminSelectList'),
        AlertWidget: () => import('./components/dashboard/AlertWidget'),
        BaseProfileLink: () => import('./components/BaseProfileLink'),
        CategorySelector: () => import('./components/knowledgebase/CategorySelector'),
        CalendarWrapper: () => import('./components/calendar/CalendarWrapper'),
        CreateCalendarEventButton: () => import('./components/calendar/CreateCalendarEventButton'),
        CalendarEntryDownloadButton: () => import('./components/CalendarEntryDownloadButton'),
        CheckedTextfield: () => import('./components/CheckedTextfield'),
        ConfirmationButton: () => import('./components/ConfirmationButton'),
        ConfirmRegistrationButton: () => import('./components/events/ConfirmRegistrationButton'),
        ContactButton: () => import('./components/ContactButton'),
        ContentList: () => import('./components/ContentList'),
        DataProtectionButton: () => import('./components/DataProtectionButton'),
        Date: () => import('./components/Date'),
        DateTimePicker: () => import('./components/DateTimePicker'),
        DropdownNavigation: () => import('./components/DropdownNavigation'),
        EventList: () => import('./components/events/EventList'),
        EventListAccordion: () => import('./components/events/EventListAccordion'),
        EventListAccordionCategory: () => import('./components/events/EventListAccordionCategory'),
        EventWidget: () => import('./components/dashboard/EventWidget'),
        ExternalEngagementTable: () => import('./components/profile/ExternalEngagementTable'),
        ExternalEngagementMigrationTable: () => import('./components/profile/ExternalEngagementMigrationTable'),
        FaqList: () => import('./components/FaqList'),
        FeatureToggleModal: () => import('./components/features/FeatureToggleModal'),
        FeedbackForm: () => import('./components/FeedbackForm'),
        FormsResultList: () => import('./components/rooms/forms/ResultList'),
        FileIcon: () => import('./components/FileIcon'),
        FileUpload: () => import('./components/FileUpload'),
        FileUploads: () => import('./components/content-editing/FileUploads'),
        FilterForm: () => import('./components/search/FilterForm'),
        FlarumLink: () => import('./components/FlarumLink'),
        FlashMessageList: () => import('./components/FlashMessageList'),
        GalleryAlbum: () => import('./components/GalleryAlbum'),
        GalleryAlbumList: () => import('./components/GalleryAlbumList'),
        ImageUpload: () => import('./components/ImageUpload'),
        InlineChat: () => import('./components/InlineChat'),
        InternalEngagementTable: () => import('./components/profile/InternalEngagementTable'),
        InternalEngagementMigrationTable: () => import('./components/profile/InternalEngagementMigrationTable'),
        JobsTable: () => import('./components/profile/JobsTable'),
        JobsMigrationTable: () => import('./components/profile/JobsMigrationTable'),
        JobsWidget: () => import('./components/dashboard/JobsWidget'),
        LabelSelector: () => import('./components/knowledgebase/LabelSelector'),
        LinkList: () => import('./components/content-editing/LinkList'),
        MarketplaceOffers: () => import('./components/MarketplaceOffers'),
        MenteeList: () => import('./components/mentoring/MenteeList'),
        MentorList: () => import('./components/mentoring/MentorList'),
        MyGroupsWidget: () => import('./components/dashboard/MyGroupsWidget'),
        MyMentorList: () => import('./components/mentoring/MyMentorList'),
        NewsList: () => import('./components/rooms/news/NewsList'),
        NicknameInput: () => import('./components/profile/NicknameInput'),
        NotificationsDropdown: () => import('./components/notifications/NotificationsDropdown'),
        NotificationsList: () => import('./components/notifications/NotificationsList'),
        NotificationCenterGroup: () => import('./components/notifications/center/NotificationCenterGroup'),
        OfferCounter: () => import('./components/OfferCounter'),
        OnboardingForm: () => import('./components/OnboardingForm'),
        PageHeader: () => import('./components/PageHeader'),
        PageList: () => import('./components/rooms/content/PageList'),
        PriorityNav: () => import('./components/PriorityNav'),
        PriorityNavSampled: () => import('./components/PriorityNavSampled'),
        PrivacyToggleButton: () => import('./components/profile/PrivacyToggleButton'),
        ProfileGroupStatisticsWidget: () => import('./components/dashboard/ProfileGroupStatisticsWidget'),
        ProfileLinkWithPreview: () => import('./components/ProfileLinkWithPreview'),
        ProfilePreview: () => import('./components/ProfilePreview'),
        ProfilePreviewBody: () => import('./components/ProfilePreviewBody'),
        ProfilePreviewPopover: () => import('./components/ProfilePreviewPopover'),
        ProfileWidget: () => import('./components/dashboard/ProfileWidget'),
        Richtext: () => import('./components/Richtext'),
        RocketchatLink: () => import('./components/RocketchatLink'),
        RoomAdminResignButton: () => import('./components/RoomAdminResignButton'),
        RoomContentAddButton: () => import('./components/content-editing/RoomContentAddButton'),
        RoomContentDeleteButton: () => import('./components/content-editing/RoomContentDeleteButton'),
        RoomDownloadList: () => import('./components/RoomDownloadList'),
        RoomJoinButton: () => import('./components/RoomJoinButton'),
        RoomLeaveButton: () => import('./components/RoomLeaveButton'),
        RoomList: () => import('./components/RoomList'),
        RoomListWithArchive: () => import('./components/RoomListWithArchive.vue'),
        RoomNewsCategoryCreator: () => import('./components/rooms/edit/CategoryCreator.vue'),
        RoomPendingMemberList: () => import('./components/rooms/user-list/PendingMemberList'),
        RoomMemberList: () => import('./components/rooms/user-list/RoomMemberList'),
        RoomModList: () => import('./components/RoomModList'),
        SearchNav: () => import('./components/search/SearchNav'),
        SearchResults: () => import('./components/search/SearchResults'),
        SearchResultsGroup: () => import('./components/search/SearchResultsGroup'),
        SideMenuNav: () => import('./components/SideMenuNav'),
        SimpleImageUpload: () => import('./components/SimpleImageUpload'),
        SimpleNewsList: () => import('./components/rooms/news/SimpleNewsList'),
        StatusGroupList: () => import('./components/StatusGroupList'),
        StudiesTable: () => import('./components/profile/StudiesTable'),
        StudiesMigrationTable: () => import('./components/profile/StudiesMigrationTable'),
        SubjectList: () => import('./components/events/SubjectList'),
        SubscriptionButtons: () => import('./components/notifications/SubscriptionButtons'),
        SubscriptionDropdown: () => import('./components/notifications/SubscriptionDropdown'),
        SvgIcon: () => import('./components/SvgIcon'),
        TagSelect: () => import('./components/TagSelect'),
        TipTeaser: () => import('./components/dashboard/TipTeaser'),
        TodoList: () => import('./components/todolist/TodoList'),
        TodoLists: () => import('./components/todolist/TodoLists'),
        UriHashControlledTabs: () => import('./components/UriHashControlledTabs'),
        UserList: () => import('./components/UserList'),
        UserListItemSkeleton: () => import('./components/rooms/user-list/ItemSkeleton'),
        UserSelect: () => import('./components/UserSelect'),
        UserSelectList: () => import('./components/UserSelectList'),
        ValidationFeedback: () => import('./components/ValidationFeedback'),
        VotingPhasesMenu: () => import('./components/VotingPhasesMenu'),
        VotingValidation: () => import('./components/VotingValidation'),
        // @TODO move this to pages/my-scholarship/index.js
        CollapsibleList: () => import('./components/my-scholarship/CollapsibleList.vue'),
        FormSchema: () => import('./components/my-scholarship/FormSchema'),
        ListItem: () => import('./components/my-scholarship/ListItem.vue'),
        ProcessList: () => import('./components/my-scholarship/ProcessList.vue'),
        TemplatesList: () => import('./components/my-scholarship/TemplatesList.vue'),
        StinaDefaultForm: () => import('./components/my-scholarship/StinaForms/StinaDefaultForm.vue'),
    },
    // define global data
    data: {
        pages: PAGES, // eslint-disable-line
        searchIsOpen: false,
        sideMenuIsOpen: false,
        onboardingFormStep: 0,
        notifStatus: false,
        profileMigration: {
            jobs: 0,
            internalEngagement: 0,
            externalEngagement: 0,
            studies: 0,
        },
        dateFormat: { year: 'numeric', month: '2-digit', day: '2-digit' },
        isMobile: breakpoints.isSmaller('lg'),
        breakpoints: breakpoints,
        scrollPosition: 0,
        unseenNotificationsCount: null,
    },
    methods: {
        activateChat (uri, title) {
            this.$refs.chat.activate(uri, title);
            return false;
        },
        registerScrollHandler () {
            const topNavigation = document.querySelector('.mobile-header > .mobile-header-top-nav');
            const bottomNavigation = document.querySelector('.mobile-header > .mobile-header-bottom-nav');
            window.addEventListener(
                'scroll',
                debounce(() => {
                    if (!this.isMobile || this.searchIsOpen) {
                        return;
                    }
                    if (this.scrollPosition > window.scrollY) {
                        if (topNavigation) {
                            topNavigation.classList.remove('mobile-header-top-nav-hidden');
                        }
                        if (bottomNavigation) {
                            bottomNavigation.classList.remove('mobile-header-bottom-nav-hidden');
                        }
                    } else {
                        if (topNavigation) {
                            topNavigation.classList.add('mobile-header-top-nav-hidden');
                        }
                        if (bottomNavigation) {
                            bottomNavigation.classList.add('mobile-header-bottom-nav-hidden');
                        }
                    }
                    this.scrollPosition = window.scrollY;
                }, 100, { leading: true }),
                { passive: true }
            );
        },
        resizeHandler () {
            this.isMobile = breakpoints.isSmaller('lg');
        },
    },
    mounted () {
        window.addEventListener('resize', this.resizeHandler);
        this.registerScrollHandler();
    },
    destroyed () {
        window.removeEventListener('resize', this.resizeHandler);
    },
};

if (process.env.NODE_ENV === 'development') {
    Vue.config.devtools = true;

    /* The new pet of the FES-CP Dev Team */
    console.log(
        '%c ' +
        '      /\\_/\\ \n' +
        '  /\\  / o o \\ \n' +
        ' //\\\\ \\~(*)~/ \n' +
        ' `  \\/   ^ / \n' +
        '    | \\|| || \n' +
        '    \\ \'|| || \n' +
        '     \\)()-()) \n' +
        '----------------------------- \n' +
        '---Say Hello to FES-CP Cat--- \n',
        'font-family:monospace'
    );
}
Vue.config.devtools = true;

if ('serviceWorker' in navigator) {
    navigator.serviceWorker
        .register('/sw.js')
        .then(() => { console.log('Service Worker Registered'); });
}
